// The step between pressing Launch and placing real phone calls.
//
// Launching schedules a call to every contact on every list attached to the
// campaign, and there is no undo: once a call is placed, it has been placed.
// Before this existed the only way to find out how many people a campaign
// would ring was to ring them.
//
// So: fetch the server's preview, show the number as the largest thing on
// screen, list anything that would stop or complicate the launch, and make the
// person type the count before the button works. The typed count is sent as
// `confirm_count` and re-checked server-side against the live number, so a
// confirmation given for one list cannot dial a larger one uploaded a moment
// later.
import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import { campaignApi, LaunchPreview } from '../services/campaignApi';

interface Props {
  open: boolean;
  campaignId: string;
  /** Called after the launch succeeds, with the scheduled calls. */
  onLaunched: (scheduledCalls: any[]) => void;
  onClose: () => void;
}

const LaunchConfirmDialog: React.FC<Props> = ({ open, campaignId, onLaunched, onClose }) => {
  const [preview, setPreview] = useState<LaunchPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let live = true;
    setLoading(true);
    setError('');
    setTyped('');
    campaignApi
      .previewCampaign(campaignId)
      .then((p) => { if (live) setPreview(p); })
      .catch((e: any) => { if (live) setError(e?.message || 'Could not check this campaign.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [open, campaignId]);

  // Deliberately an exact match on the number, not a checkbox. Typing "250"
  // requires reading that it is 250 people.
  const confirmed = !!preview && typed.trim() === String(preview.will_call);
  const canLaunch = !!preview && preview.can_launch && confirmed && !launching;

  const handleLaunch = async () => {
    if (!preview) return;
    setLaunching(true);
    setError('');
    try {
      const calls = await campaignApi.launchCampaign(campaignId, preview.will_call);
      onLaunched(calls);
    } catch (e: any) {
      // The server re-checks the count. If the list changed under us it says so
      // here, and nothing has been dialled.
      setError(e?.message || 'The campaign was not launched.');
      setLaunching(false);
    }
  };

  const removed = preview ? preview.contacts_total - preview.will_call : 0;

  return (
    <Dialog open={open} onClose={launching ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <PhoneForwardedIcon color="primary" />
        Launch this campaign?
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && preview && (
          <>
            <Box
              sx={{
                textAlign: 'center',
                py: 2.5,
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.07),
              }}
            >
              <Typography
                sx={{ fontSize: 56, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}
                color="primary"
              >
                {preview.will_call}
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                {preview.will_call === 1 ? 'person will be called' : 'people will be called'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                from {preview.from_number}
              </Typography>
            </Box>

            {removed > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
                {preview.contacts_total} contacts on the lists; {removed} removed as duplicates or
                unusable numbers.
              </Typography>
            )}

            {preview.blocking.length > 0 && (
              <Alert severity="error" icon={<BlockIcon />} sx={{ mt: 2.5 }}>
                <AlertTitle>This campaign cannot be launched</AlertTitle>
                <List dense disablePadding>
                  {preview.blocking.map((b, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0.2 }}>
                      <ListItemText primary={b} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {preview.warnings.length > 0 && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 2 }}>
                <AlertTitle>Worth checking first</AlertTitle>
                <List dense disablePadding>
                  {preview.warnings.map((w, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0.2 }}>
                      <ListItemText primary={w} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {preview.duplicates.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Numbers on more than one list
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Each of these will be called once, not once per list.
                </Typography>
                {preview.duplicates.slice(0, 6).map((d, i) => (
                  <Chip
                    key={i}
                    size="small"
                    label={`${d.phone} — ${d.names.join(' / ')}`}
                    sx={{ mr: 0.6, mb: 0.6 }}
                  />
                ))}
                {preview.duplicates.length > 6 && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    and {preview.duplicates.length - 6} more
                  </Typography>
                )}
              </Box>
            )}

            {preview.can_launch && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Typography variant="body2" gutterBottom>
                  Calls begin as soon as you launch and cannot be recalled. Type{' '}
                  <strong>{preview.will_call}</strong> to confirm.
                </Typography>
                <TextField
                  id="launch-confirm-count"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={String(preview.will_call)}
                  size="small"
                  autoComplete="off"
                  inputProps={{ inputMode: 'numeric', 'aria-label': 'Type the number of people to confirm' }}
                  sx={{ mt: 0.5, width: 160 }}
                  disabled={launching}
                />
              </>
            )}
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={launching}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleLaunch}
          disabled={!canLaunch}
          startIcon={launching ? <CircularProgress size={16} color="inherit" /> : <PhoneForwardedIcon />}
        >
          {launching
            ? 'Launching…'
            : preview
            ? `Call ${preview.will_call} ${preview.will_call === 1 ? 'person' : 'people'}`
            : 'Launch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LaunchConfirmDialog;
